'use client';
import { jsx as _jsx } from "react/jsx-runtime";
import { createContext, useCallback, useEffect, useState } from 'react';
import { useCookie } from '../hooks/use-cookie.js';
export const defaultSettings = {
    theme: 'zinc',
    darkMode: 'system',
    radius: 0.5,
    layout: 'vertical',
    locale: 'en',
};
export const SettingsContext = createContext(undefined);
/**
 * Context for the settings
 * @example
 * ```ts
 * import { SettingsProvider } from "@cloud/shared/ui"
 *
 * function App() {
 *   return (
 *     <SettingsProvider locale="en">
 *       <App />
 *     </SettingsProvider>
 *   )
 * }
 * ```
 *
 * @example
 * ```tsx
 * // Disable theme selection
 * <SettingsProvider locale="en" settingsConfig={{ theme: false }}>
 *   <App />
 * </SettingsProvider>
 * ```
 */
export function SettingsProvider({ locale, children, settingsConfig = {
    theme: true,
    darkMode: true,
    radius: true,
    layout: true,
    locale: true,
}, }) {
    const [storedSettings, setStoredSettings, deleteStoredSettings] = useCookie('settings');
    const [settings, setSettings] = useState(null);
    useEffect(() => {
        if (storedSettings) {
            const parsedSettings = JSON.parse(storedSettings);
            setSettings({ ...defaultSettings, locale, ...parsedSettings });
        }
        else {
            setSettings({ ...defaultSettings, locale });
        }
    }, [storedSettings, locale]);
    const updateSettings = useCallback((newSettings) => {
        // Filter and only store enabled settings
        const filteredSettings = filterSettings(newSettings, settingsConfig);
        setStoredSettings(JSON.stringify(filteredSettings));
        setSettings(newSettings);
    }, [setStoredSettings, settingsConfig]);
    const resetSettings = useCallback(() => {
        deleteStoredSettings();
        setSettings({ ...defaultSettings, locale });
    }, [deleteStoredSettings, locale]);
    // Render children only when settings are ready
    if (!settings) {
        return null;
    }
    return (_jsx(SettingsContext.Provider, { value: { settings, updateSettings, resetSettings, settingsConfig }, children: children }));
}
/**
 * Filters settings based on the configuration
 * Only includes settings that are enabled in the config
 */
function filterSettings(settings, config) {
    const filtered = {};
    if (config.theme !== false) {
        filtered.theme = settings.theme;
    }
    if (config.darkMode !== false) {
        filtered.darkMode = settings.darkMode;
    }
    if (config.radius !== false) {
        filtered.radius = settings.radius;
    }
    if (config.layout !== false) {
        filtered.layout = settings.layout;
    }
    if (config.locale !== false) {
        filtered.locale = settings.locale;
    }
    return filtered;
}
